import { conflicts } from '../data/conflicts';
import {
  categoryLabel,
  categoryColor,
  formatDeaths,
  conflictDuration,
  sortConflicts,
  filterConflicts,
} from '../utils';
import type { Conflict, ConflictCategory, Region } from '../types';
import { glossarySpan } from '../tooltip';

export function renderConflictsView(container: HTMLElement, search: string): void {
  let sortBy: 'deaths' | 'duration' | 'name' | 'category' = 'deaths';
  let ascending = false;
  let regionFilter: Region | '' = '';
  let categoryFilter: ConflictCategory | '' = '';

  const wrapper = document.createElement('div');
  wrapper.className = 'view-container';
  container.appendChild(wrapper);

  function renderTable() {
    let filtered = filterConflicts(conflicts, {
      region: regionFilter || undefined,
      category: categoryFilter || undefined,
      search: search || undefined,
    });
    filtered = sortConflicts(filtered, sortBy, ascending);

    wrapper.innerHTML = `
      <div class="view-header">
        <h2>Active Armed Conflicts (${filtered.length})</h2>
        <p>Every known armed conflict worldwide, classified by ${glossarySpan('Intensity level', 'intensity')}. Click any conflict for details.</p>
      </div>

      <div class="filter-bar">
        <select class="filter-select" id="filter-region">
          <option value="">All Regions</option>
          <option value="Africa"${regionFilter === 'Africa' ? ' selected' : ''}>Africa</option>
          <option value="Americas"${regionFilter === 'Americas' ? ' selected' : ''}>Americas</option>
          <option value="Asia"${regionFilter === 'Asia' ? ' selected' : ''}>Asia</option>
          <option value="Europe"${regionFilter === 'Europe' ? ' selected' : ''}>Europe</option>
          <option value="Middle East"${regionFilter === 'Middle East' ? ' selected' : ''}>Middle East</option>
          <option value="Oceania"${regionFilter === 'Oceania' ? ' selected' : ''}>Oceania</option>
        </select>
        <select class="filter-select" id="filter-category">
          <option value="">All Categories</option>
          <option value="major_war"${categoryFilter === 'major_war' ? ' selected' : ''}>Major War</option>
          <option value="minor_war"${categoryFilter === 'minor_war' ? ' selected' : ''}>Minor War</option>
          <option value="conflict"${categoryFilter === 'conflict' ? ' selected' : ''}>Conflict</option>
          <option value="skirmish"${categoryFilter === 'skirmish' ? ' selected' : ''}>Skirmish</option>
        </select>
      </div>

      <table class="data-table">
        <thead>
          <tr>
            <th class="${sortBy === 'name' ? 'sorted' : ''}" data-sort="name">Conflict</th>
            <th class="${sortBy === 'category' ? 'sorted' : ''}" data-sort="category">Category</th>
            <th>Region</th>
            <th>Countries</th>
            <th class="${sortBy === 'duration' ? 'sorted' : ''}" data-sort="duration">Since</th>
            <th class="${sortBy === 'deaths' ? 'sorted' : ''}" data-sort="deaths">Deaths (2025)</th>
            <th>Cumulative</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${filtered.map(c => conflictRow(c)).join('')}
        </tbody>
      </table>
    `;

    // Sort handlers
    wrapper.querySelectorAll('th[data-sort]').forEach(th => {
      th.addEventListener('click', () => {
        const newSort = (th as HTMLElement).dataset.sort as typeof sortBy;
        if (newSort === sortBy) {
          ascending = !ascending;
        } else {
          sortBy = newSort;
          ascending = false;
        }
        renderTable();
      });
    });

    // Filter handlers
    document.getElementById('filter-region')?.addEventListener('change', (e) => {
      regionFilter = (e.target as HTMLSelectElement).value as Region | '';
      renderTable();
    });
    document.getElementById('filter-category')?.addEventListener('change', (e) => {
      categoryFilter = (e.target as HTMLSelectElement).value as ConflictCategory | '';
      renderTable();
    });

    // Row click → expand detail
    wrapper.querySelectorAll('.clickable-name').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.id!;
        const c = conflicts.find(x => x.id === id);
        if (c) showConflictDetail(c);
      });
    });
  }

  function conflictRow(c: Conflict): string {
    return `
      <tr>
        <td><button class="clickable-name" data-id="${c.id}">${c.name}</button></td>
        <td><span class="category-pill" style="background: ${categoryColor(c.category)}20; color: ${categoryColor(c.category)}">${categoryLabel(c.category)}</span></td>
        <td>${c.region}</td>
        <td style="font-size: var(--font-size-xs); color: var(--text-secondary); max-width: 200px;">${c.countryNames.slice(0, 3).join(', ')}${c.countryNames.length > 3 ? ` +${c.countryNames.length - 3}` : ''}</td>
        <td class="mono">${c.startYear}</td>
        <td class="mono" style="color: ${categoryColor(c.category)}">${formatDeaths(c.deaths2025)}</td>
        <td class="mono">${formatDeaths(c.deathsCumulative)}</td>
        <td style="font-size: var(--font-size-xs); color: var(--text-secondary); max-width: 250px;">${c.status}</td>
      </tr>
    `;
  }

  function showConflictDetail(c: Conflict) {
    wrapper.innerHTML = `
      <div style="margin-bottom: var(--space-lg);">
        <button class="clickable-name" id="back-btn" style="color: var(--text-secondary); font-size: var(--font-size-sm);">← Back to all conflicts</button>
      </div>
      <div class="conflict-detail">
        <div style="margin-bottom: 12px;">
          <span class="category-pill" style="background: ${categoryColor(c.category)}20; color: ${categoryColor(c.category)}">${categoryLabel(c.category)}</span>
          <span style="color: var(--text-tertiary); font-size: var(--font-size-sm); margin-left: 8px;">${c.region}</span>
        </div>
        <h2>${c.name}</h2>
        <p class="cd-description">${c.description}</p>
        <p style="font-size: var(--font-size-sm); color: var(--text-tertiary); margin-bottom: var(--space-lg);">
          <strong>Status:</strong> ${c.status}
        </p>

        <div class="cd-stats">
          <div class="cd-stat"><div class="value">${conflictDuration(c.startYear)}</div><div class="label">Duration</div></div>
          <div class="cd-stat"><div class="value">${formatDeaths(c.deaths2025)}</div><div class="label">Deaths (2025)</div></div>
          <div class="cd-stat"><div class="value">${formatDeaths(c.deathsCumulative)}</div><div class="label">Cumulative</div></div>
          <div class="cd-stat"><div class="value">${c.startYear}</div><div class="label">Start Year</div></div>
          <div class="cd-stat"><div class="value">${c.type}</div><div class="label">Type</div></div>
        </div>

        <div class="cd-parties">
          <div class="cd-party">
            <h4>Side A</h4>
            <ul>${c.sideA.map(s => `<li>${s}</li>`).join('')}</ul>
          </div>
          <div class="cd-party">
            <h4>Side B</h4>
            <ul>${c.sideB.map(s => `<li>${s}</li>`).join('')}</ul>
          </div>
        </div>

        <div class="detail-section">
          <h3>Countries involved</h3>
          <p style="color: var(--text-secondary); font-size: var(--font-size-sm);">
            ${c.countryNames.join(', ')}
          </p>
        </div>
      </div>
    `;

    document.getElementById('back-btn')!.addEventListener('click', renderTable);
  }

  renderTable();
}
